import { TimeLog } from '../../types';
import { format } from 'date-fns';
import { liveWorkedSeconds } from './timeclock-utils';
import { getTargetType, getTargetLabel } from '../../services/timeLogAdapter';

/**
 * Pure timesheet/payroll helpers. Kept free of React so the hour math has a
 * single home — the shift editor and the timesheet view both import from here,
 * which is what prevents the net-vs-gross duration bug from coming back.
 *
 * De un turno solo se preguntan DOS cosas, y hay una función para cada una:
 *
 *   · `effectiveMinutes(log, now)` — lo que vale AHORA MISMO, con el turno
 *     abierto corriendo. Es el número de las pantallas que cuentan lo que está
 *     pasando: barra de vitales, tarjetas de cuadrilla, mapa del reloj.
 *   · `billedMinutes(log)` — lo que se puede COBRAR: solo turnos cerrados, un
 *     turno abierto vale 0. Es el número de todo lo que produce dinero.
 *
 * Llegaron a convivir siete reglas distintas para lo mismo, y una cuadrilla en
 * obra leía 6.2 h en la barra de vitales, 0.0 h en la pestaña de Horas de esa
 * misma obra y 6.7 h en el mapa. La diferencia siempre era la misma: si el
 * turno abierto contaba, y si se le creía a `durationMinutes` o se derivaba de
 * los sellos de entrada/salida/lonche.
 */

/** Stable display key for an employee from a TimeLog (email → userId → name). */
export const employeeKeyFor = (log: TimeLog): string => {
    if (log.userEmail) return log.userEmail.toLowerCase();
    if (log.userId) return log.userId;
    return log.userName || 'unknown';
};

/** Completed-lunch minutes between two timestamps (0 when lunch wasn't recorded). */
export const computeLunchMinutes = (
    lunchStart?: string | null,
    lunchEnd?: string | null,
): number => {
    if (!lunchStart || !lunchEnd) return 0;
    const mins = (new Date(lunchEnd).getTime() - new Date(lunchStart).getTime()) / 60000;
    return Math.max(0, Math.round(mins));
};

/**
 * THE payroll formula: net worked minutes = (clockOut − clockIn) − lunch.
 * Both the timesheet view and the shift editor MUST use this so a shift's
 * hours never depend on who computed them. Returns 0 for an open shift.
 */
export const computeNetMinutes = (
    clockIn: string,
    clockOut?: string | null,
    lunchStart?: string | null,
    lunchEnd?: string | null,
): number => {
    if (!clockOut) return 0;
    const gross = (new Date(clockOut).getTime() - new Date(clockIn).getTime()) / 60000;
    const net = gross - computeLunchMinutes(lunchStart, lunchEnd);
    return Math.max(0, Math.round(net));
};

/** `duration_minutes` se guarda redondeado y los sellos de tiempo no, así que
 *  un turno legítimo puede pasarse por segundos. */
const TOLERANCIA_MIN = 1;

/** Minutos de entrada a salida, sin descontar nada. 0 si falta la salida o si
 *  alguno de los dos sellos no es una fecha. */
const minutosBrutos = (clockIn: string, clockOut?: string | null): number => {
    if (!clockOut) return 0;
    const mins = (new Date(clockOut).getTime() - new Date(clockIn).getTime()) / 60000;
    return Number.isFinite(mins) ? Math.max(0, mins) : 0;
};

/**
 * ¿Se le puede creer el `durationMinutes` guardado a un turno YA CERRADO?
 *
 * Se le cree cuando cabe dentro del turno: el cierre resta el lonche —incluso
 * el que nadie cerró, que no se puede derivar de los sellos— y el cierre
 * automático guarda su propio neto. Se descarta cuando no puede ser verdad:
 *
 *   · más minutos de los que duró el turno de entrada a salida;
 *   · un 0 con horas de por medio. Eso es lo que escribe REABRIR un turno
 *     (`{clockOut: null, durationMinutes: 0}` — cero, no nulo): en cuanto
 *     alguien le ponía salida, todo lector que preguntara `!= null` lo daba
 *     por un turno cerrado de cero horas y esas horas no volvían nunca.
 *
 * `null` = no hay número creíble, hay que derivar de los sellos.
 */
const duracionGuardadaCreible = (log: TimeLog, brutos: number): number | null => {
    const d = log.durationMinutes;
    if (typeof d !== 'number' || !Number.isFinite(d) || d < 0) return null;
    if (d > brutos + TOLERANCIA_MIN) return null;
    if (d === 0 && brutos > TOLERANCIA_MIN) return null;
    return d;
};

/**
 * El turno lo cerró el barrendero sin saber cuándo terminó de verdad.
 *
 * `closeStaleShifts` ya no inventa 480 minutos: escribe la salida en el mismo
 * instante que la entrada y la duración en NULO. Pero cobrable eso vale 0 —no
 * se puede cobrar lo que nadie sabe—, y un 0 en una columna de totales no se
 * distingue de un turno que de verdad duró nada. Esta es la marca que los
 * distingue: quien pinte horas debe enseñar «—» y no «0.0» cuando sea cierta,
 * y quien sume debe poder decir cuántas filas está dejando fuera.
 *
 * Sin esto, el arreglo sería cambiar 480 minutos inventados por 0 inventados.
 */
export const necesitaRevision = (log: TimeLog): boolean =>
    log.status === 'Auto-closed' && (log.durationMinutes === null || log.durationMinutes === undefined);

/**
 * Minutos COBRABLES de un turno: solo cuenta lo ya cerrado.
 *
 * Un turno abierto vale 0 — no porque no se esté trabajando, sino porque
 * todavía no es un hecho: meter un reloj corriendo en una cifra de dinero la
 * haría cambiar cada minuto. Este es el número de todo lo que produce dinero.
 */
export const billedMinutes = (log: TimeLog): number => {
    if (!log.clockOut) return 0;
    /* Con una entrada ilegible, `minutosBrutos` devuelve 0 —traga el NaN—, eso
       hace que NINGUNA duración guardada positiva se crea, y derivar termina en
       `Math.max(0, Math.round(NaN))`, que es NaN y no 0. Ese NaN llegaba hasta
       el costo de mano de obra de la obra y pintaba «$NaN» con un margen NaN.
       Postgres no puede producirlo (`clock_in` es NOT NULL), pero la caché
       optimista y los turnos de demostración no son la base. */
    if (!Number.isFinite(new Date(log.clockIn).getTime())) {
        const d = log.durationMinutes;
        return typeof d === 'number' && Number.isFinite(d) ? Math.max(0, d) : 0;
    }
    const brutos = minutosBrutos(log.clockIn, log.clockOut);
    const guardada = duracionGuardadaCreible(log, brutos);
    if (guardada !== null) return guardada;
    /* Un turno cerrado con la duración en NULO daba 0 en unos lectores y horas
       de verdad en otros. Se deriva. El lonche que nadie cerró se da por
       terminado a la salida: si no, el turno cobraría también el descanso. */
    return computeNetMinutes(log.clockIn, log.clockOut, log.lunchStart, log.lunchEnd || log.clockOut);
};

/**
 * Lo que el turno vale AHORA MISMO, con el turno abierto corriendo.
 *
 * Del turno abierto no se mira `durationMinutes` ni de reojo: reabrir un turno
 * lo deja en 0 con la salida en nulo, y creerle es exactamente como una
 * cuadrilla que llevaba media jornada en obra marcaba 0.0 h.
 */
export const effectiveMinutes = (log: TimeLog, now: Date = new Date()): number => {
    if (log.clockOut) return billedMinutes(log);
    return Math.round(liveWorkedSeconds(log, now) / 60);
};

/**
 * @deprecated Pregunta qué número quieres: `billedMinutes` (lo cobrable) o
 * `effectiveMinutes` (lo que va corrido ahora). Se queda un release para no
 * romper en silencio a quien todavía lo importa.
 */
export const netMinutes = (log: TimeLog): number => billedMinutes(log);

/** Completed-lunch minutes for a stored TimeLog. */
export const lunchMinutes = (log: TimeLog): number =>
    computeLunchMinutes(log.lunchStart, log.lunchEnd);

/** Minutes → "7.5h" label. */
export const fmtHours = (mins: number): string => `${(mins / 60).toFixed(1)}h`;

/** Overtime threshold: minutes worked beyond this in one ISO week are overtime. */
export const OT_WEEKLY_CAP = 40 * 60;

/**
 * Overtime minutes for one employee's logs: time worked beyond OT_WEEKLY_CAP
 * within any single ISO week, summed across weeks. Bucketing by ISO week keeps
 * it correct even when the selected date range spans several weeks.
 *
 * Cuenta minutos COBRABLES: el turno que sigue abierto no genera horas extra
 * todavía — la nómina se paga sobre lo cerrado, no sobre un reloj corriendo.
 */
export const computeOvertimeMinutes = (logs: TimeLog[]): number => {
    const byWeek = new Map<string, number>();
    for (const log of logs) {
        const wk = format(new Date(log.clockIn), 'RRRR-II');
        byWeek.set(wk, (byWeek.get(wk) || 0) + billedMinutes(log));
    }
    return Array.from(byWeek.values())
        .reduce((sum, wkMin) => sum + Math.max(0, wkMin - OT_WEEKLY_CAP), 0);
};

/**
 * Clave del TURNO al que pertenece un tramo.
 *
 * `||` y no `??` por lo mismo que en `getTargetId`: la cadena vacía no es NULL,
 * y con `??` todos los tramos con `shiftGroupId: ''` caerían en un mismo turno
 * gigante. Las filas anteriores a los tramos no traen grupo: cada una es un
 * turno de una, y su propio id le sirve de clave.
 */
export const shiftGroupKey = (log: TimeLog): string => log.shiftGroupId || log.id;

/**
 * Cuántos TURNOS hay, no cuántas filas.
 *
 * Un día de taller → obra A → obra B son tres filas y UN turno. Contar filas le
 * decía al dueño que esa persona fichó tres veces ese día, y el promedio de
 * horas por turno salía a un tercio de la jornada real.
 */
export const countShiftGroups = (logs: TimeLog[]): number =>
    new Set(logs.map(shiftGroupKey)).size;

/**
 * En qué cubeta cae un turno para el dueño: lo que se le cobra a alguien
 * (`billable`), lo que cuesta tener taller (`overhead`), y lo que nadie llegó a
 * clasificar (`unclassified`).
 *
 * `'orphaned'` NO es un valor de la base: es el resultado del cruce con las
 * obras vivas. Una fila que apunta a una obra ya borrada sigue diciendo
 * `target_type = 'project'`, y darla por facturable es lo que impide que Horas
 * cuadre con Finanzas — en producción son 6 filas con 46 h. Tiene que verse.
 *
 * `'unclassified'` no es `'overhead'`. Las 88 filas `general` significan «el
 * menú estaba vacío», no «estuvo en el taller»; meterlas en gastos generales
 * inventaría un dato que nadie fichó.
 *
 * `liveProjectIds` en `null` = todavía no se sabe qué obras viven (el contexto
 * de proyectos sigue cargando). Sin censo no se puede acusar a nadie de
 * huérfano, así que la obra cuenta como facturable — que es lo que era antes de
 * este cruce. Un `Set` vacío es distinto: ahí el censo SÍ se conoce y está
 * vacío, y una obra que no está en él de verdad no existe.
 */
export type HoursClass = 'billable' | 'overhead' | 'unclassified' | 'orphaned';

export const hoursClassOf = (
    log: TimeLog,
    liveProjectIds: ReadonlySet<string> | null,
): HoursClass => {
    switch (getTargetType(log)) {
        case 'project':
            if (!liveProjectIds) return 'billable';
            return log.projectId && liveProjectIds.has(log.projectId) ? 'billable' : 'orphaned';
        case 'contract':
            return 'billable';
        case 'office':
            return 'overhead';
        default:
            return 'unclassified';
    }
};

export interface MinutesSplit {
    total: number;
    billable: number;
    overhead: number;
    unclassified: number;
    orphaned: number;
}

/**
 * Reparto de los minutos de un periodo por cubeta. Las cuatro partes suman
 * `total` exactamente — es lo que hace que los mosaicos se puedan leer juntos.
 *
 * Cuenta `effectiveMinutes` y no `billedMinutes` A PROPÓSITO. Estos mosaicos
 * describen un PERIODO («últimos 7 días»), y ese periodo incluye el turno que
 * está corriendo ahora mismo: con lo cobrable, tres personas fichadas en el
 * taller desde las 7 de la mañana dejan el mosaico de taller en 0.0 h y la
 * pantalla parece rota. Es exactamente la queja que separó las dos funciones.
 *
 * Lo que sí es dinero —el CSV de nómina, el overtime, el costo de mano de obra
 * de la obra— se queda en `billedMinutes`: una cifra que se paga no puede
 * cambiar cada minuto.
 */
export const splitMinutes = (
    logs: TimeLog[],
    now: Date,
    liveProjectIds: ReadonlySet<string> | null,
): MinutesSplit => {
    const out: MinutesSplit = { total: 0, billable: 0, overhead: 0, unclassified: 0, orphaned: 0 };
    for (const log of logs) {
        const mins = effectiveMinutes(log, now);
        out.total += mins;
        out[hoursClassOf(log, liveProjectIds)] += mins;
    }
    return out;
};

/**
 * Excel y Sheets abren como FÓRMULA cualquier celda que empiece por `=`, `+`,
 * `-` o `@`. `userName` es el nombre que la persona tiene puesto en su cuenta
 * de Google, así que lo elige ella: un trabajador llamado
 * `=HYPERLINK("http…","Nómina")` convierte el CSV de nómina en un ataque a
 * quien lo abra. El apóstrofo delante obliga a la hoja a tratarlo como texto y
 * no se ve en la celda.
 *
 * El `\r` entra en el escape ahora que las filas se unen con CRLF: un campo con
 * un retorno suelto partía la fila en dos.
 */
const csvEscape = (v: string | number | null | undefined): string => {
    if (v === null || v === undefined) return '';
    let s = String(v);
    if (/^[=+\-@]/.test(s)) s = `'${s}`;
    if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
};

/** Sin BOM, Excel abre el CSV con la codificación del sistema y «Nuñez» sale
 *  «NuÃ±ez». Es la primera queja que llega de cualquier exportación.
 *  Se construye por código y no se pega el carácter: pegado tal cual es
 *  invisible en el editor y cualquiera lo borra sin saber que lo borra. */
const CSV_BOM = String.fromCharCode(0xFEFF);

/**
 * Build a payroll-ready CSV from a list of time logs.
 *
 * Las columnas nuevas van SIEMPRE al final y las viejas no se mueven: nómina
 * mapea por posición, y reordenar aquí le cambia el sueldo a alguien sin que
 * nada falle.
 *
 * `liveProjectIds` es el censo de obras vivas (ver `hoursClassOf`); `null`
 * cuando todavía no se conoce.
 */
export const buildCsv = (logs: TimeLog[], liveProjectIds: ReadonlySet<string> | null): string => {
    const headers = [
        'Name', 'Email', 'Role', 'Date',
        'Clock In', 'Clock Out',
        'Lunch (min)', 'Work (min)', 'Hours',
        'Project', 'Off-Site', 'Status',
        'Target Type', 'Target', 'Billable', 'Shift Group', 'Segment', 'Auto-Closed',
    ];
    const rows = logs.map(log => {
        const clase = hoursClassOf(log, liveProjectIds);
        return [
            log.userName,
            log.userEmail || '',
            log.userRole || '',
            format(new Date(log.clockIn), 'yyyy-MM-dd'),
            format(new Date(log.clockIn), 'HH:mm'),
            log.clockOut ? format(new Date(log.clockOut), 'HH:mm') : '',
            lunchMinutes(log),
            billedMinutes(log),
            (billedMinutes(log) / 60).toFixed(2),
            log.projectName || '',
            log.isOffSite ? 'yes' : 'no',
            log.clockOut ? (log.status === 'Auto-closed' ? 'Auto-closed' : 'Closed') : 'Active',
            getTargetType(log),
            /* `projectName` está contaminado —guarda nombres de cliente de
               contratos y el literal 'General Shop Time'—, así que la columna
               que se lee es esta, no la de arriba. */
            getTargetLabel(log),
            /* Tres valores y no un sí/no: el turno de una obra borrada tampoco
               es facturable, pero si cae en el montón de los 'no' desaparece, y
               son 46 h que Finanzas sí tiene contadas en algún sitio. */
            clase === 'billable' ? 'yes' : clase === 'orphaned' ? 'orphaned' : 'no',
            shiftGroupKey(log),
            log.segmentSeq ?? 1,
            /* 'review' = lo cerró el barrendero sin saber cuándo terminó, así
               que su 'Work (min)' es un 0 que no es una jornada de cero. */
            log.status === 'Auto-closed' ? (necesitaRevision(log) ? 'review' : 'yes') : 'no',
        ];
    });
    return CSV_BOM + [headers, ...rows].map(r => r.map(csvEscape).join(',')).join('\r\n');
};

/** Trigger a browser download of a CSV string. */
export const downloadCsv = (csv: string, filename: string): void => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};
